import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { BarcodeElements, ParsedBarcodeElement } from './barcode-elements';
import { cleanCodeString } from './scan-parser.utils';
import { scanSwitch } from './scan-switch.utils';
import { matchAndProcessBarcode } from './scan.utils';

@Injectable({
    providedIn: 'root',
})
export class ScanUtilities {
    public constructor(@Inject(LogInstanceToken) private readonly log: LogInterface) {}

    private identifyApplicationIdentifier(unSanitizedCodeString: string): ParsedBarcodeElement {
        const codeString = cleanCodeString(unSanitizedCodeString);
        const scanResult = scanSwitch(codeString);
        const codeStringToReturn = cleanCodeString(scanResult.codeStringToReturn);
        return new ParsedBarcodeElement(scanResult.element, codeStringToReturn);
    }

    public identifyBarcodeElementsByApplicationIdentifier(
        unSanitizedCodeString: string,
        applicationIdentifier: string,
    ): BarcodeElements | undefined {
        const parsedBarcodeElementList: BarcodeElements[] = [];
        let barcodeScanResult = unSanitizedCodeString.replace(/[|]/g, '');

        try {
            while (barcodeScanResult.length > 0) {
                const barcodeElement = this.identifyApplicationIdentifier(barcodeScanResult);
                if (barcodeElement) {
                    barcodeScanResult = barcodeElement.codeString;
                    parsedBarcodeElementList.push(barcodeElement.element);
                }
            }
            return parsedBarcodeElementList.find((element) => element.ai === applicationIdentifier);
        } catch (error) {
            this.log.warn(
                `ScanUtilities: identifyBarcodeElementsByApplicationIdentifier: Could not find application identifier '${applicationIdentifier}': ${JSON.stringify(
                    error.message,
                )}`,
            );
        }

        return undefined;
    }

    /**
     * Does basically the same as identifyApplicationIdentifier, but returns the original barcode if no element with the appId could be identified.
     *
     * @param {string} unSanitizedCodeString the read barcode
     * @param {string} applicationIdentifier the application identifier to search for
     * @returns {ParsedBarcodeElement} the parsed barcode element
     */
    public tryIdentifyBarcodeElementsByApplicationIdentifier(
        unSanitizedCodeString: string,
        applicationIdentifier: string,
    ): string {
        const barcodeElementResult = this.identifyBarcodeElementsByApplicationIdentifier(
            unSanitizedCodeString,
            applicationIdentifier,
        );
        return barcodeElementResult?.data?.toString() ?? unSanitizedCodeString;
    }

    public getBarcodeDisplayString(barcode: string, appId: string, pattern?: string, format?: string): string {
        barcode = this.tryIdentifyBarcodeElementsByApplicationIdentifier(barcode, appId);
        try {
            return matchAndProcessBarcode(barcode, pattern, format);
        } catch {
            return barcode;
        }
    }
}
